import React from 'react';
import { Box, Typography, Button, Breadcrumbs, Link } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import AddIcon from '@mui/icons-material/Add';
import { useTheme } from '@mui/material/styles';

const PageHeader = ({
  title,
  subtitle,
  breadcrumbs = [],
  actionLabel,
  actionIcon,
  onAction,
  secondaryAction,
  secondaryLabel,
  children,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', md: 'flex-start' },
        mb: { xs: 2, md: 3 },
        flexWrap: 'wrap',
        gap: 2,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        {breadcrumbs.length > 0 && (
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" sx={{ color: isDark ? '#718096' : '#718096' }} />}
            sx={{ mb: 1, '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap', overflow: 'hidden' } }}
          >
            {breadcrumbs.map((crumb, index) => (
              <Link
                key={index}
                href={crumb.href || '#'}
                underline="hover"
                sx={{
                  color: index === breadcrumbs.length - 1
                    ? (isDark ? '#ffffff' : '#1a202c')
                    : (isDark ? '#718096' : '#718096'),
                  fontSize: { xs: '0.8125rem', md: '0.875rem' },
                  fontWeight: index === breadcrumbs.length - 1 ? 500 : 400,
                }}
              >
                {crumb.label}
              </Link>
            ))}
          </Breadcrumbs>
        )}
        <Typography
          variant="h4"
          sx={{
            fontSize: { xs: '1.25rem', md: '1.5rem' },
            fontWeight: 700,
            color: isDark ? '#ffffff' : '#1a202c',
            mb: 0.5,
            fontFamily: "'Inter', sans-serif",
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            variant="body2"
            sx={{
              color: isDark ? '#cbd5e1' : '#718096',
              fontSize: { xs: '0.8125rem', md: '0.875rem' },
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', width: { xs: '100%', md: 'auto' } }}>
        {children}
        {secondaryAction && (
          <Button
            variant="outlined"
            onClick={secondaryAction}
            sx={{
              borderColor: isDark ? 'rgba(255, 255, 255, 0.2)' : '#e2e8f0',
              color: isDark ? '#ffffff' : '#1a202c',
              textTransform: 'none',
              fontWeight: 500,
              px: 2.5,
              py: 1,
              minHeight: 44,
              borderRadius: '8px',
              flex: { xs: 1, md: 'none' },
              '&:hover': {
                borderColor: isDark ? 'rgba(255, 255, 255, 0.3)' : '#cbd5e1',
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f7fafc',
              },
            }}
          >
            {secondaryLabel}
          </Button>
        )}
        {actionLabel && (
          <Button
            variant="contained"
            startIcon={actionIcon || <AddIcon />}
            onClick={onAction}
            sx={{
              backgroundColor: isDark ? '#6366f1' : '#1976d2',
              textTransform: 'none',
              fontWeight: 600,
              px: 2.5,
              py: 1,
              minHeight: 44,
              borderRadius: '8px',
              flex: { xs: 1, md: 'none' },
              boxShadow: isDark
                ? '0 4px 12px rgba(99, 102, 241, 0.3)'
                : '0 2px 8px rgba(25, 118, 210, 0.3)',
              '&:hover': {
                backgroundColor: isDark ? '#4f46e5' : '#1565c0',
                boxShadow: isDark
                  ? '0 6px 16px rgba(99, 102, 241, 0.4)'
                  : '0 4px 12px rgba(25, 118, 210, 0.4)',
              },
            }}
          >
            {actionLabel}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default PageHeader;
